<template>
    <div class="col-sm-12 editor-form">
      <h2 class="editor-header">{{ additionalCostUpdate.id > 0 ? $t('additional_cost.edit') : $t('additional_cost.add') }}</h2>
      <div class="col-sm-12 form-data nopads">
        <!-- Users -->
        <div
          v-if="additionalCostUpdate.id < 1 && isObserver"
          class="nopads pr-1 pl-1"
        >
          <span class="span-title">
            {{ $t('work_time.user') }}
          </span>
          <select
            v-model="additionalCostUpdate.user.id"
            class="form-select form-select-sm mb-3"
            :disabled="!users || users.length < 1"
          >
              <option
                :value="null"
                disabled
              >
                {{ $t('work_time.select_user') }}
              </option>
            <option
              v-for="item in users"
              :key="item.id"
              :value="item.id"
            >
              {{ item.last_name }}, {{ item.first_name }}
            </option>
          </select>
        </div>
        <div v-if="additionalCostUpdate.id > 0">
          <h3>
            {{ additionalCostUpdate.user.first_name + ' ' + additionalCostUpdate.user.last_name }}
          </h3>
        </div>
        <div class="pr-1 pl-1">
          <div
            class="nopads"
            label-for="type"
          >
            <span class="search-title">{{ $t('delivery_note.contract') }}</span>
            <select
              v-model="selectedContract"
              class="form-select form-select-sm"
              :disabled="!!loading"
            >
              <option :value="null">
                {{ $t('delivery_note.form.select_contract') }}
              </option>
              <option
                v-for="contract in contracts"
                :key="contract.id"
                :value="contract"
              >
                {{ contract.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="pr-1 pl-1">
            <contract-order-multiselect
                ref="orderFilter"
                :contract-ids="contractOptions"
                :single-mode="true"
                :pre-selected-order="additionalCostUpdate.work_order ? additionalCostUpdate.work_order.id : null"
                @orders-selected="setOrder"
            />
        </div>

        <div class="pr-1 pl-1">
          <div
            class="nopads"
            label-for="type"
          >
            <span class="span-title">{{ $t('additional_cost.type') }}</span>
            <select
              id="type"
              v-model="additionalCostUpdate.type"
              class="form-select form-select-sm sm-3"
            >
              <option :value="null">
                {{ $t('work_permission.select_type') }}
              </option>
              <option
                v-for="option in getAdditionalCostType()"
                :key="option.value"
                :value="option.value"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <div
            class="title"
            label-for="amount"
          >
            <span class="span-title">{{ $t('additional_cost.amount') }}</span>
            <input
              id="amount"
              v-model="additionalCostUpdate.amount"
              :placeholder="$t('additional_cost.amount')"
              class="form-control form-control-sm"
              type="number"
              step="any"
            >
          </div>
        </div>

        <div>
          <div
            class="title"
            label-for="unit"
          >
            <span class="span-title">{{ $t('additional_cost.unit') }}</span>
            <input
              id="unit"
              v-model="additionalCostUpdate.unit"
              :placeholder="$t('additional_cost.unit')"
              class="form-control form-control-sm"
              type="text"
            >
          </div>
        </div>

        <div>
          <div
            class="title"
            label-for="unit_price"
          >
            <span class="span-title">{{ $t('additional_cost.unit_price') }}</span>
            <input
              id="unit_price"
              v-model="additionalCostUpdate.unit_price"
              :placeholder="$t('additional_cost.unit_price')"
              class="form-control form-control-sm"
              type="number"
              step="any"
            >
          </div>
        </div>

        <!-- Info -->
        <div class="col-sm-12 nopads">
          <span class="span-title">
            {{ $t('work_time.note') }}
          </span>
          <textarea
            id="info"
            v-model="additionalCostUpdate.info"
            class="form-control col-12"
            :placeholder="$t('work_time.note')"
            :rows="4"
          />
        </div>

        <div class="col-sm-12 button-container">
          <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
            {{ $t('common.cancel') }}
          </button>
          <button v-if="isObserver && additionalCostUpdate.id>0" class="btn btn-outline-danger form-button" :disabled="!!loading" @click.stop="handleApproval(3)">
              {{ $t('work_permission.reject') }}
          </button>
          <button v-if="isObserver && additionalCostUpdate.id>0" class="btn btn-outline-success form-button" :disabled="!!loading" @click.stop="handleApproval(2)">
            {{ $t('daily_allowance.approve_allowance') }}
          </button>
          <button class="btn btn-success form-button" :disabled="!!loading || !selectedContract || !selectedOrder" @click.stop="submit">
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="!!loading"
      id="loader"
      class="spinner"
    />
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {additionalCostHelper} from "@/components/mixins/AdditionalCostMixin"
import {orderMixin} from "@/components/mixins/OrderMixin";
import {inject} from "vue";
import ContractOrderMultiselect from "../order/ContractOrderMultiselect.vue";

export default {
    name: 'AdditionalCostEditor',
    components: {ContractOrderMultiselect},
    mixins: [restApi, additionalCostHelper, orderMixin],
    props: {
        isObserver: Boolean,
        additionalCost: {
            type: Object,
            default: null
        }
    },
    emits: ['close', 'closeAndUpdate'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: 0,
            users: [],
            additionalCostUpdate: this.newAdditionalCost(this.additionalCost),
            contracts: [],
            selectedContract: null,
            selectedOrder: null,
            contractOptions: []

        }
    },

    watch: {
        selectedContract() {
           this.contractOptions = this.selectedContract ? [this.selectedContract.id] : []
        },
        additionalCost() {
            if (this.additionalCost) {
                this.additionalCostUpdate = this.additionalCost
                if (this.additionalCost.work_order) {
                    this.selectedOrder = this.additionalCost.work_order
                    this.selectedContract = this.additionalCost.work_order.contract
                } else if (this.additionalCostUpdate.contract) {
                    this.selectedContract = this.additionalCost.contract
                }
            }
        },
    },

    mounted: function () {
        this.additionalCostUpdate = this.additionalCost ? this.additionalCost : this.newAdditionalCost()
        if (this.additionalCost && this.additionalCost.work_order) {
            this.selectedOrder = this.additionalCost.work_order
            this.selectedContract = this.additionalCost.work_order.contract
        } else if (this.additionalCost?.contract) {
            this.selectedContract = this.additionalCost.contract
        }
        this.fetchUsers()
        this.fetchContracts()
    },

    methods: {
        setOrder(order) {
            this.selectedOrder = order
        },
        fetchContracts: function () {
            this.loading++
            let params = {open: 1}
            this.restFetchParams(this.contractUrl, params, this.handleResponse)
        },
        handleResponse: function (response) {
            this.loading--
            this.contracts = response ? response.data : []
            if (this.selectedContract && !this.contracts.find(item => item.id === this.selectedContract.id)) {
                this.contracts.push(this.selectedContract)
            }
            if (this.selectedContract) {
                this.$nextTick(() => this.selectedContract = this.contracts.find(item => item.id === this.selectedContract.id))
            }
        },
        fetchUsers: function () {
            this.loading++
            this.restFetch(this.userUrl, this.handleUsers)
        },
        handleUsers: function (response) {
            this.loading--
            this.users = response.data
        },
        submit: function () {
            if (!this.selectedContract) {
                this.showAlert(this.$t('ins_wizard.select_contract'))
                return
            } else {
                this.additionalCostUpdate.contract = {id: this.selectedContract}
            }
            if (!this.selectedOrder) {
                this.showAlert(this.$t('ins_wizard.select_order'))
                return
            } else {
                this.additionalCostUpdate.work_order = this.selectedOrder
            }
            if (!this.additionalCostUpdate.user) {
                this.showAlert(this.$t('trip_list.select_user'))
                return
            }
            if (!this.additionalCostUpdate.type) {
                this.showAlert( this.$t('additional_cost.select_type'))
                return
            }
            if (!this.additionalCostUpdate.amount) {
                this.showAlert(this.$t('additional_cost.enter_amount'))
                return
            }
            if (!this.additionalCostUpdate.unit) {
                this.showAlert(this.$t('additional_cost.enter_unit'))
                return
            }
            if (!this.additionalCostUpdate.unit_price) {
                this.showAlert(this.$t('additional_cost.enter_unit_price'))
                return
            }
            if (this.selectedContract) {
                this.additionalCostUpdate.contract = {id: this.selectedContract}
            }
            this.loading++
            if (this.additionalCostUpdate.id > 0) {
                this.restUpdate(this.additionalCostUrl, this.additionalCostUpdate, this.success, this.fail)
            } else {
                this.restAdd(this.additionalCostUrl, this.additionalCostUpdate, this.success, this.fail)
            }
        },
        success: function () {
            this.loading--
            this.$emit('closeAndUpdate')
        },
        fail: function () {
            this.loading--
            if (this.additionalCostUpdate.id < 0) {
                this.showAlert(this.$t('common.alert_add_failed'))
            } else {
                this.showAlert(this.$t('common.alert_update_failed'))
            }
        },
        handleApproval: function (status) {
            this.restUpdate(this.additionalCostUrl + "/approval/" + this.additionalCostUpdate.id,
                {status: status}, this.success, this.fail)
        }
    }
}
</script>
